import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, Search } from 'lucide-react';
import { api, type SelfReportLinkRow } from '@/lib/api';
import { cn } from '@/lib/utils';

function labelFor(r: SelfReportLinkRow): string {
  return r.displayName?.trim() ? r.displayName.trim() : r.name;
}

function matches(row: SelfReportLinkRow, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    row.name.toLowerCase().includes(needle) ||
    (row.displayName?.toLowerCase().includes(needle) ?? false) ||
    row.rank.toLowerCase().includes(needle)
  );
}

export default function PlayerHubPicker({
  variant = 'desktop',
}: {
  variant?: 'desktop' | 'mobile' | 'icon';
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const links = useQuery({
    queryKey: ['self-report-links-picker'],
    queryFn: api.listSelfReportLinks,
    enabled: open,
    staleTime: 60_000,
  });

  const rows = useMemo(() => {
    const list = links.data?.rows ?? [];
    const filtered = list.filter((r) => matches(r, q));
    return filtered
      .slice()
      .sort((a, b) => labelFor(a).localeCompare(labelFor(b)))
      .slice(0, 50);
  }, [links.data, q]);

  useEffect(() => {
    setActiveIdx(0);
  }, [q, open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const openHub = (token: string) => {
    navigate(`/players/${encodeURIComponent(token)}/self-report`);
    setOpen(false);
    setQ('');
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[activeIdx];
      if (row) openHub(row.token);
    }
  };

  const isMobile = variant === 'mobile';
  const isIcon = variant === 'icon';

  return (
    <div ref={ref} className={cn('relative', isMobile ? 'w-full' : '')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          isIcon
            ? 'inline-flex items-center justify-center rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-white'
            : isMobile
              ? 'flex w-full items-center gap-2 rounded px-3 py-2 text-base text-slate-200 hover:bg-slate-800 hover:text-white'
              : 'inline-flex items-center gap-1 rounded px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-white',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Switch player"
        title="Switch player"
      >
        {isIcon ? (
          <ArrowLeftRight size={14} />
        ) : (
          <>
            <Search size={isMobile ? 14 : 12} />
            Open player hub
          </>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          className={cn(
            'z-50 overflow-hidden rounded-md border border-slate-800 bg-slate-900 shadow-lg',
            isMobile
              ? 'mt-2 w-full'
              : isIcon
                ? 'fixed left-1/2 top-14 mt-1 w-[min(18rem,calc(100vw-1rem))] -translate-x-1/2'
                : 'absolute right-0 top-full mt-1 w-72',
          )}
        >
          <div className="border-b border-slate-800 p-2">
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Search player…"
              className="w-full rounded border border-slate-800 bg-slate-950 px-2 py-1 text-sm text-slate-100 outline-none focus:border-indigo-500"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto text-sm">
            {links.isLoading ? (
              <li className="px-3 py-2 text-slate-500">Loading…</li>
            ) : rows.length === 0 ? (
              <li className="px-3 py-2 text-slate-500">No matches.</li>
            ) : (
              rows.map((r, i) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => openHub(r.token)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left transition',
                      i === activeIdx
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-200 hover:bg-slate-800 hover:text-white',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {labelFor(r)}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                        {r.rank}
                      </span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
